import { Controller } from "@hotwired/stimulus"
// Handles expand/collapse for team companies and dev history accordion
// on the project show page.
export default class extends Controller {
  static targets = ["companyGroup", "companyBody", "companyChevron",
                     "devGroup", "devBody", "devChevron",
                     "processingLabel"]

  static values = {
    justCreated: { type: Boolean, default: false },
    projectId: { type: Number, default: 0 }
  }

  connect() {
    if (this.justCreatedValue) {
      this.processingLabelTargets.forEach(el => {
        // Skip the spinner on the owner row — ProjectCreationForm seeds owner
        // ProjectMembers synchronously, so that row is already complete.
        const group = el.closest("[data-team-owner-group]")
        if (group?.dataset.teamOwnerGroup === "true") return
        el.classList.remove("hidden")
      })
      this._pollProcessing()
      this._showToast("Project created successfully!")
    }
  }

  disconnect() {
    if (this._pollTimer) clearTimeout(this._pollTimer)
  }

  // ── Processing poll ───────────────────────────────────────────────────

  _pollProcessing() {
    const projectId = this.projectIdValue
    if (!projectId) {
      this._refreshContent()
      return
    }

    fetch(`/projects/${projectId}/processing_status.json`, {
      headers: { Accept: "application/json" }
    })
      .then(r => r.json())
      .then(data => {
        if (data.processing) {
          this._pollTimer = setTimeout(() => this._pollProcessing(), 2000)
        } else {
          // Small delay so final DB writes commit before we fetch fresh HTML
          this._pollTimer = setTimeout(() => this._refreshContent(), 1000)
        }
      })
      .catch(() => {
        // On error, fall back to a single refresh after 3s
        this._pollTimer = setTimeout(() => this._refreshContent(), 3000)
      })
  }

  _refreshContent() {
    const url = new URL(window.location)
    url.searchParams.delete("just_created")

    fetch(url.toString(), { headers: { Accept: "text/html" } })
      .then(r => r.text())
      .then(html => {
        const doc = new DOMParser().parseFromString(html, "text/html")
        const fresh = doc.querySelector(".project-show-content")
        if (fresh) this.element.outerHTML = fresh.outerHTML
        history.replaceState({}, "", url.toString())
      })
  }

  // ── Team section ──────────────────────────────────────────────────────

  toggleCompany(event) {
    const group = event.currentTarget.closest("[data-project-show-target='companyGroup']")
    if (!group) return

    const body = group.querySelector("[data-project-show-target='companyBody']")
    const chevron = group.querySelector("[data-project-show-target='companyChevron']")
    this._toggle(body, chevron)
  }

  toggleAllCompanies() {
    const anyCollapsed = this.companyBodyTargets.some(
      body => body.style.maxHeight === "0px" || !body.style.maxHeight
    )
    if (anyCollapsed) {
      this.expandAll()
    } else {
      this.collapseAll()
    }
  }

  expandAll() {
    this.companyBodyTargets.forEach((body, i) => {
      body.style.maxHeight = body.scrollHeight + "px"
      body.style.opacity = "1"
      if (this.companyChevronTargets[i]) {
        this.companyChevronTargets[i].style.transform = "rotate(90deg)"
      }
      body.addEventListener("transitionend", () => {
        if (body.style.maxHeight !== "0px") {
          body.style.overflow = "visible"
        }
      }, { once: true })
    })
  }

  collapseAll() {
    this.companyBodyTargets.forEach((body, i) => {
      body.style.overflow = "hidden"
      body.style.maxHeight = "0px"
      body.style.opacity = "0"
      if (this.companyChevronTargets[i]) {
        this.companyChevronTargets[i].style.transform = ""
      }
    })
  }

  // ── Dev history accordion ─────────────────────────────────────────────

  toggleDev(event) {
    const group = event.currentTarget.closest("[data-project-show-target='devGroup']")
    if (!group) return

    const body = group.querySelector("[data-project-show-target='devBody']")
    const chevron = group.querySelector("[data-project-show-target='devChevron']")
    this._toggle(body, chevron)
  }

  // ── Shared toggle logic ───────────────────────────────────────────────

  _toggle(body, chevron) {
    if (!body) return

    const isCollapsed = body.style.maxHeight === "0px" || !body.style.maxHeight

    if (isCollapsed) {
      body.style.maxHeight = body.scrollHeight + "px"
      body.style.opacity = "1"
      if (chevron) chevron.style.transform = "rotate(90deg)"
      body.addEventListener("transitionend", () => {
        if (body.style.maxHeight !== "0px") {
          body.style.overflow = "visible"
        }
      }, { once: true })
    } else {
      body.style.overflow = "hidden"
      body.style.maxHeight = "0px"
      body.style.opacity = "0"
      if (chevron) chevron.style.transform = ""
    }
  }

  _showToast(message) {
    const el = document.createElement("div")
    el.className = "flash-toast-notice"
    el.setAttribute("data-controller", "flash")
    el.setAttribute("data-flash-dismiss-after-value", "5000")
    el.setAttribute("role", "alert")
    const iconDiv = document.createElement("div")
    iconDiv.className = "shrink-0"
    iconDiv.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"/></svg>'
    const msgP = document.createElement("p")
    msgP.className = "flex-1 text-sm"
    msgP.textContent = message
    el.appendChild(iconDiv)
    el.appendChild(msgP)
    document.body.prepend(el)
  }
}
